/**
 * The skeleton (03 §1, §5.1 step 1): slice the strip into a drop band in the south, three
 * to five approach bands, the defensive belt and the goal zone in the north.
 *
 * Bands depend on the seed and the map length only, never on the re-roll attempt, so the
 * validator can recompute them from a finished `MissionDef`.
 */

import { Pcg32 } from '../sim/rng';
import { BANDS, MAP, type Range } from './tuning';
import type { Band } from './types';

const BAND_STREAM = 53;

const even = (v: number): number => Math.round(v / MAP.tile) * MAP.tile;
const within = (rng: Pcg32, r: Range): number => even(rng.range(r[0], r[1]));

/** South to north: drop, approach 1..n, belt, goal. */
export function sliceBands(seed: number, sizeZ: number): Band[] {
  const rng = new Pcg32(seed, BAND_STREAM);
  const south = sizeZ - MAP.badlands;
  let drop = within(rng, BANDS.drop);
  let goal = within(rng, BANDS.goal);
  const belt = within(rng, BANDS.belt);
  let rest = south - drop - goal - belt;

  const [minCount, maxCount] = BANDS.approachCount;
  const [minDepth, maxDepth] = BANDS.approach;
  const counts: number[] = [];
  for (let n = minCount; n <= maxCount; n++) if (rest / n >= minDepth && rest / n <= maxDepth) counts.push(n);
  let count = counts.length > 0 ? rng.pick(counts) : Math.min(maxCount, Math.max(minCount, Math.round(rest / 40)));
  if (counts.length === 0) {
    // A very long or very short strip: the drop band and the goal zone absorb the difference.
    const target = Math.min(maxDepth, Math.max(minDepth, rest / count)) * count;
    const spare = even((rest - target) / 2);
    drop += spare;
    goal += spare;
    rest = south - drop - goal - belt;
    count = Math.min(maxCount, Math.max(minCount, count));
  }

  // Uneven approach bands: jitter each depth, then take the mean back out so they still sum.
  const jitter: number[] = [];
  for (let i = 0; i < count; i++) jitter.push(rng.range(-4, 4));
  const mean = jitter.reduce((a, b) => a + b, 0) / count;

  const bands: Band[] = [{ kind: 'drop', z0: south - drop, z1: south }];
  let z1 = south - drop;
  const floor = goal + belt;
  for (let i = 0; i < count; i++) {
    const depth = rest / count + (jitter[i] ?? 0) - mean;
    const z0 = i === count - 1 ? floor : Math.max(floor + (count - 1 - i) * minDepth, even(z1 - depth));
    bands.push({ kind: 'approach', z0, z1 });
    z1 = z0;
  }
  bands.push({ kind: 'belt', z0: goal, z1: floor });
  bands.push({ kind: 'goal', z0: 0, z1: goal });
  return bands;
}
